import { supabase } from "./supabase";

export const REFERENCE_CERTIFICATE_BUCKET = "reference-standard-certificates";

const ASSET_SELECT = `
  id,
  asset_kind,
  operational_status,
  review_status,
  name,
  brand_model,
  serial,
  location,
  service_description,
  calibration_frequency_months,
  calibration_provider,
  estimated_lead_time,
  inventory_calibration_date,
  inventory_due_date,
  source_filename,
  source_row,
  metadata,
  notes,
  created_at,
  updated_at,
  codes:calibration_standard_codes (
    id, code, family, is_primary, active, valid_from, valid_until, notes
  ),
  capabilities:calibration_standard_capabilities (
    id, magnitude, function_key, function_label, capability_role,
    range_min, range_max, unit, resolution, active, metadata, notes
  ),
  certificate_links:calibration_standard_certificate_assets (
    asset_role,
    certificate:calibration_standard_certificates (
      id, certificate_no, document_type, issuer, accreditation,
      calibration_date, issue_date, valid_until, certificate_status,
      storage_path, original_filename, mime_type, file_size, sha256,
      traceability, coverage_factor, confidence_level, notes,
      reviewed_at, reviewed_by, created_at, updated_at,
      results:calibration_standard_certificate_results (
        id, capability_id, point_no, range_label, reference_value,
        indicated_value, correction, error, uncertainty_expanded,
        coverage_factor, confidence_level, unit, interpolation_policy,
        coefficients, metadata, notes, sort_order
      )
    )
  )
`;

function cleanObject(value) {
  return Object.fromEntries(
    Object.entries(value || {}).filter(([, field]) => field !== undefined)
  );
}

function normalizeText(value) {
  const text = String(value ?? "").trim();
  return text || null;
}

function finiteOrNull(value) {
  if (value === "" || value === null || value === undefined) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function currentCertificate(asset) {
  const certificates = (asset?.certificate_links || [])
    .map((link) => ({ ...link.certificate, asset_role: link.asset_role }))
    .filter((certificate) => certificate?.id)
    .sort((a, b) => {
      const aDate = a.calibration_date || a.issue_date || a.created_at || "";
      const bDate = b.calibration_date || b.issue_date || b.created_at || "";
      return String(bDate).localeCompare(String(aDate));
    });
  return certificates[0] || null;
}

export async function listReferenceStandardAssets() {
  const { data, error } = await supabase
    .from("calibration_standard_assets")
    .select(ASSET_SELECT)
    .order("name", { ascending: true });
  if (error) throw error;

  return (data || []).map((asset) => ({
    ...asset,
    current_certificate: currentCertificate(asset),
  }));
}

export async function createReferenceStandardAsset({
  asset,
  code,
  capability,
}) {
  const { data: authData } = await supabase.auth.getUser();
  const userId = authData?.user?.id || null;

  const { data: createdAsset, error: assetError } = await supabase
    .from("calibration_standard_assets")
    .insert({
      ...cleanObject(asset),
      name: normalizeText(asset?.name),
      brand_model: normalizeText(asset?.brand_model),
      serial: normalizeText(asset?.serial),
      location: normalizeText(asset?.location),
      service_description: normalizeText(asset?.service_description),
      calibration_provider: normalizeText(asset?.calibration_provider),
      estimated_lead_time: normalizeText(asset?.estimated_lead_time),
      calibration_frequency_months: finiteOrNull(
        asset?.calibration_frequency_months
      ),
      review_status: asset?.review_status || "pending",
      created_by: userId,
    })
    .select("id")
    .single();
  if (assetError) throw assetError;

  try {
    const { data: createdCode, error: codeError } = await supabase
      .from("calibration_standard_codes")
      .insert({
        asset_id: createdAsset.id,
        code: String(code?.code || "").trim().toUpperCase(),
        family: String(code?.family || "").trim().toLowerCase(),
        is_primary: true,
        active: asset?.operational_status !== "retired",
        created_by: userId,
      })
      .select("id")
      .single();
    if (codeError) throw codeError;

    const { error: capabilityError } = await supabase
      .from("calibration_standard_capabilities")
      .insert({
        asset_id: createdAsset.id,
        code_id: createdCode.id,
        magnitude: String(
          capability?.magnitude || code?.family || ""
        ).trim().toLowerCase(),
        function_key: normalizeText(capability?.function_key),
        function_label: normalizeText(
          capability?.function_label || asset?.service_description
        ),
        capability_role: capability?.capability_role || "reference",
        range_min: finiteOrNull(capability?.range_min),
        range_max: finiteOrNull(capability?.range_max),
        unit: normalizeText(capability?.unit),
        resolution: finiteOrNull(capability?.resolution),
        active: asset?.operational_status === "active",
        created_by: userId,
      });
    if (capabilityError) throw capabilityError;
  } catch (error) {
    await supabase
      .from("calibration_standard_assets")
      .delete()
      .eq("id", createdAsset.id);
    throw error;
  }

  return createdAsset.id;
}

export async function updateReferenceStandardAsset(assetId, patch) {
  const { data: authData } = await supabase.auth.getUser();
  const { error } = await supabase
    .from("calibration_standard_assets")
    .update({
      ...cleanObject(patch),
      updated_by: authData?.user?.id || null,
    })
    .eq("id", assetId);
  if (error) throw error;
}

export async function updateReferenceStandardCode(codeId, patch) {
  const { error } = await supabase
    .from("calibration_standard_codes")
    .update(cleanObject(patch))
    .eq("id", codeId);
  if (error) throw error;
}

export async function updateReferenceStandardCapability(capabilityId, patch) {
  const { error } = await supabase
    .from("calibration_standard_capabilities")
    .update({
      ...cleanObject(patch),
      range_min: finiteOrNull(patch?.range_min),
      range_max: finiteOrNull(patch?.range_max),
      resolution: finiteOrNull(patch?.resolution),
    })
    .eq("id", capabilityId);
  if (error) throw error;
}

export async function createReferenceStandardCapability(assetId, capability) {
  const { data: code } = await supabase
    .from("calibration_standard_codes")
    .select("id")
    .eq("asset_id", assetId)
    .eq("active", true)
    .order("is_primary", { ascending: false })
    .limit(1)
    .maybeSingle();
  const { error } = await supabase
    .from("calibration_standard_capabilities")
    .insert({
      asset_id: assetId,
      code_id: code?.id || null,
      magnitude: String(capability?.magnitude || "").trim().toLowerCase(),
      function_key: normalizeText(capability?.function_key) || `manual_${Date.now()}`,
      function_label: normalizeText(capability?.function_label),
      capability_role: capability?.capability_role || "reference",
      range_min: finiteOrNull(capability?.range_min),
      range_max: finiteOrNull(capability?.range_max),
      unit: normalizeText(capability?.unit),
      resolution: finiteOrNull(capability?.resolution),
      active: capability?.active !== false,
    });
  if (error) throw error;
}

function safeStorageName(filename) {
  return String(filename || "certificado.pdf")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9._-]+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 140);
}

async function fileSha256(file) {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

export async function createReferenceStandardCertificate({
  assetId,
  assetRole = "main",
  certificate,
  file,
  capabilityId = null,
  results = [],
  referenceInputMode = "corrected_value",
}) {
  const { data: authData } = await supabase.auth.getUser();
  const userId = authData?.user?.id || null;
  let storagePath = null;
  let sha256 = null;
  let createdId = null;

  if (certificate?.certificate_status === "verified") {
    if (!file) throw new Error("Un certificado verificado debe incluir el PDF original.");
    if (!certificate?.calibration_date || !certificate?.valid_until) {
      throw new Error("Un certificado verificado requiere fecha de calibración y vencimiento.");
    }
    if (capabilityId && !results.length) {
      throw new Error("Cargá al menos un resultado certificado antes de verificar el documento.");
    }
  }

  if (file) {
    if (file.type && file.type !== "application/pdf") {
      throw new Error("El certificado debe ser un archivo PDF.");
    }
    if (file.size > 25 * 1024 * 1024) {
      throw new Error("El PDF supera el límite de 25 MB.");
    }
    sha256 = await fileSha256(file);
    storagePath = `${assetId}/${Date.now()}-${safeStorageName(file.name)}`;
    const { error: uploadError } = await supabase.storage
      .from(REFERENCE_CERTIFICATE_BUCKET)
      .upload(storagePath, file, {
        contentType: "application/pdf",
        upsert: false,
      });
    if (uploadError) throw uploadError;
  }

  try {
    const isVerified = certificate?.certificate_status === "verified";
    const { data: created, error: certificateError } = await supabase
      .from("calibration_standard_certificates")
      .insert({
        certificate_no: String(certificate?.certificate_no || "").trim(),
        document_type:
          certificate?.document_type || "calibration_certificate",
        issuer: normalizeText(certificate?.issuer),
        accreditation: normalizeText(certificate?.accreditation),
        calibration_date: certificate?.calibration_date || null,
        issue_date: certificate?.issue_date || null,
        valid_until: certificate?.valid_until || null,
        certificate_status: certificate?.certificate_status || "draft",
        storage_path: storagePath,
        original_filename: file?.name || null,
        mime_type: file?.type || null,
        file_size: file?.size || null,
        sha256,
        traceability: normalizeText(certificate?.traceability),
        coverage_factor: finiteOrNull(certificate?.coverage_factor),
        confidence_level: finiteOrNull(certificate?.confidence_level),
        notes: normalizeText(certificate?.notes),
        reviewed_at: isVerified ? new Date().toISOString() : null,
        reviewed_by: isVerified ? userId : null,
        created_by: userId,
      })
      .select("id")
      .single();
    if (certificateError) throw certificateError;
    createdId = created.id;

    const { error: linkError } = await supabase
      .from("calibration_standard_certificate_assets")
      .insert({
        certificate_id: created.id,
        asset_id: assetId,
        asset_role: assetRole,
      });
    if (linkError) throw linkError;

    if (capabilityId && results.length) {
      const normalizedResults = results.map((result, index) => ({
        certificate_id: created.id,
        capability_id: capabilityId,
        point_no: index + 1,
        reference_value: finiteOrNull(result.reference_value),
        indicated_value: finiteOrNull(result.indicated_value),
        correction: finiteOrNull(result.correction),
        error: finiteOrNull(result.error),
        uncertainty_expanded: finiteOrNull(result.uncertainty_expanded),
        coverage_factor: finiteOrNull(certificate?.coverage_factor) || 2,
        confidence_level: finiteOrNull(certificate?.confidence_level),
        unit: normalizeText(result.unit),
        interpolation_policy: result.interpolation_policy || "exact_points",
        metadata: {
          reference_input_mode: referenceInputMode,
        },
        sort_order: index + 1,
        created_by: userId,
      }));
      if (normalizedResults.some((result) => result.uncertainty_expanded === null)) {
        throw new Error("Cada resultado debe informar su incertidumbre expandida U.");
      }
      const { error: resultsError } = await supabase
        .from("calibration_standard_certificate_results")
        .insert(normalizedResults);
      if (resultsError) throw resultsError;

      const capability = (await supabase
        .from("calibration_standard_capabilities")
        .select("metadata")
        .eq("id", capabilityId)
        .single()).data;
      const { error: capabilityError } = await supabase
        .from("calibration_standard_capabilities")
        .update({
          metadata: {
            ...(capability?.metadata || {}),
            reference_input_mode: referenceInputMode,
          },
        })
        .eq("id", capabilityId);
      if (capabilityError) throw capabilityError;
    }

    return created.id;
  } catch (error) {
    if (createdId) {
      await supabase
        .from("calibration_standard_certificates")
        .delete()
        .eq("id", createdId);
    }
    if (storagePath) {
      await supabase.storage
        .from(REFERENCE_CERTIFICATE_BUCKET)
        .remove([storagePath]);
    }
    throw error;
  }
}

export async function getReferenceCertificateSignedUrl(storagePath) {
  if (!storagePath) return null;
  const { data, error } = await supabase.storage
    .from(REFERENCE_CERTIFICATE_BUCKET)
    .createSignedUrl(storagePath, 300);
  if (error) throw error;
  return data?.signedUrl || null;
}

export function isReferenceStandardsMigrationMissing(error) {
  return (
    error?.code === "42P01" ||
    error?.code === "PGRST205" ||
    /calibration_standard_assets|schema cache/i.test(
      String(error?.message || error || "")
    )
  );
}
